import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API, Storage } from 'aws-amplify';
import gqlTag from 'graphql-tag';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { PageHeader } from '../../components/page-header';
import YouTube from 'react-youtube';
import Vimeo from '@u-wave/react-vimeo';

const gql: any = gqlTag;

declare const global: {
  me: {
    id: string;
    orgID: string;
    imageName?: string;
    name: string;
  };
  trainingVideos: Record<string, any[]> | null;
};

interface LessonData {
  id: string;
  name: string;
  description: string;
  url: string;
  urlType: string;
  youTubeURL: string | null;
  order: number;
  imageName: string;
  imageType: string;
  posterImageName: string | null;
  category: string;
  createdAt: string;
  videoURL?: string;
}

export default function TrainingVideoPage() {
  const { lessonId, category } = useParams<{
    lessonId: string;
    category: string;
  }>();
  const navigate = useNavigate();
  const [lessonData, setLessonData] = useState<LessonData | null>(null);

  const getLesson = async () => {
    const myQuery = gql`
      query GetLessonDetail($id: ID!) {
        getLessonDetail(id: $id) {
          id
          name
          description
          url
          urlType
          youTubeURL
          order
          imageName
          imageType
          posterImageName
          category
          createdAt
        }
      }
    `;

    try {
      const res: any = await API.graphql({
        query: myQuery,
        variables: { id: lessonId },
      });
      const lesson = res.data.getLessonDetail;
      if (lesson.posterImageName === '') {
        lesson.posterImageName = null;
      }
      if (lesson.youTubeURL === '') {
        lesson.youTubeURL = null;
      }
      if (!lesson.youTubeURL) {
        lesson.videoURL = await Storage.get(lesson.imageName);
      } else {
        lesson.videoURL = 'null';
      }
      setLessonData(lesson);
    } catch (error) {
      console.error('Error fetching lesson:', error);
    }
  };

  const convertDate = (date: string) => {
    const originalDate = new Date(date);
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(originalDate);
  };

  useEffect(() => {
    if (!lessonId || !category) {
      return;
    }
    if (global.trainingVideos && global.trainingVideos[category]) {
      setLessonData(
        global.trainingVideos[category].find(
          (vid: any) => vid.id === lessonId
        ) || null
      );
    } else {
      getLesson();
    }
  }, [lessonId, category]);

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Training"
        subtitle={lessonData?.name}
        avatar={{ src: global.me?.imageName, name: global.me?.name }}
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Back navigation */}
          <button
            onClick={() => navigate(`/v2/training/${category}`)}
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-4" />
          </button>

          {lessonData ? (
            <div className="space-y-6">
              {/* Video Player */}
              <div className="w-full flex justify-center">
                <div className="w-full max-w-[960px]">
                  {/* PDF viewer */}
                  {lessonData.imageType === 'pdf' && !lessonData.youTubeURL && (
                    <iframe
                      src={lessonData.videoURL}
                      className="w-full aspect-[960/585] border-0 rounded-lg"
                      title={lessonData.name}
                    />
                  )}

                  {/* Native video player */}
                  {lessonData.imageType !== 'pdf' &&
                    !(lessonData.youTubeURL || lessonData.url) && (
                      <video
                        autoPlay
                        className="w-full rounded-lg"
                        controls
                        controlsList="nodownload"
                      >
                        <source src={lessonData.videoURL} />
                      </video>
                    )}

                  {/* YouTube player */}
                  {(lessonData.youTubeURL ||
                    (lessonData.url && lessonData.urlType === 'youtube')) && (
                    <YouTubePlayer
                      videoId={
                        lessonData.youTubeURL
                          ? lessonData.youTubeURL
                          : lessonData.url
                      }
                    />
                  )}

                  {/* Vimeo player */}
                  {lessonData.url && lessonData.urlType === 'vimeo' && (
                    <VimeoPlayer video={lessonData.url} />
                  )}

                  {/* Facebook player */}
                  {lessonData.url && lessonData.urlType === 'facebook' && (
                    <FacebookPlayer url={lessonData.url} />
                  )}
                </div>
              </div>

              {/* Lesson info */}
              <div className="max-w-[960px] mx-auto space-y-4">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold text-foreground">
                    {lessonData.name}
                  </h2>
                  <span className="text-sm text-muted-foreground">
                    {convertDate(lessonData.createdAt)}
                  </span>
                </div>
                {lessonData.description && (
                  <p className="text-sm text-muted-foreground leading-relaxed break-words">
                    {lessonData.description}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── YouTube Player ──────────────────────────────────────────

function YouTubePlayer({ videoId }: { videoId: string }) {
  const opts = {
    height: '585',
    width: '100%',
    playerVars: {
      autoplay: 1,
    },
  };

  return (
    <div className="w-full aspect-video rounded-lg overflow-hidden">
      <YouTube
        videoId={videoId}
        opts={opts}
        className="w-full h-full"
        iframeClassName="w-full h-full"
      />
    </div>
  );
}

// ── Vimeo Player ──────────────────────────────────────────

function VimeoPlayer({ video }: { video: string }) {
  return (
    <div className="w-full aspect-video rounded-lg overflow-hidden">
      <Vimeo key={video} video={video} autoplay={true} responsive={true} />
    </div>
  );
}

// ── Facebook Player ──────────────────────────────────────────

function FacebookPlayer({ url }: { url: string }) {
  const [embedError, setEmbedError] = useState(false);
  const embedSrc = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(
    url
  )}&show_text=false&width=476`;

  if (embedError) {
    return (
      <div className="w-full max-w-[476px] mx-auto aspect-[476/750] flex flex-col items-center justify-center bg-muted rounded-lg gap-3 p-6">
        <p className="text-base text-foreground">
          This video could not be embedded.
        </p>
        <p className="text-sm text-muted-foreground text-center">
          Facebook Reels and some private videos do not support external
          embedding.
        </p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 px-6 py-2.5 bg-[#1877F2] text-white rounded-lg font-semibold text-sm hover:bg-[#1877F2]/90 transition-colors"
        >
          Watch on Facebook
        </a>
      </div>
    );
  }

  return (
    <div className="flex justify-center">
      <iframe
        src={embedSrc}
        width="476"
        height="750"
        className="border-0 overflow-hidden rounded-lg"
        scrolling="no"
        frameBorder="0"
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        allowFullScreen
        title="Facebook Video"
        onError={() => setEmbedError(true)}
        onLoad={(e: React.SyntheticEvent<HTMLIFrameElement>) => {
          try {
            const iframe = e.target as HTMLIFrameElement;
            if (iframe.contentDocument?.body?.innerText === '') {
              setEmbedError(true);
            }
          } catch (_) {
            // Cross-origin - embed loaded, which means it's working
          }
        }}
      />
    </div>
  );
}
